/**
 * Venom Altar asset manifest (render-only). Generated from the built kit GLBs and the Codex
 * textures; see assets/CREDITS.md for sources and licences (all CC0 or project-generated).
 *
 * Models: each kit GLB holds many props as top-level nodes named by asset id. Load the kit once
 * and use `gltf.scene.getObjectByName(node)`. Units: 1 unit = 1 yd for KayKit (walls are 4 yd
 * tall, matching dungeon.ts). Kenney Nature props are authored around a 1-unit tile, so use the
 * suggested `scale` (about 3.5 to 4). Animations and skins are stripped; primitives are joined
 * per material: one material per KayKit kit (shared gradient atlas), flat colours for Kenney.
 *
 * `size` is [width x, height y, depth z] in yd at scale 1. `pivot` is '<vertical>-<horizontal>':
 * vertical is base (the origin sits on the ground), mid (wall-mounted or hanging) or top;
 * horizontal is centre or offset (see boundsMin/boundsMax). The model faces +z.
 * Textures: tileYd is the suggested world size of one texture repeat.
 */

export type VenomAssetKind = 'model' | 'texture' | 'decal';

export interface VenomModelAsset {
  id: string;
  kind: 'model';
  pack: 'kaykit-dungeon-remastered' | 'kaykit-halloween-bits' | 'kenney-nature-kit';
  /** Path relative to BASE_URL. */
  file: string;
  /** Node name inside the kit GLB. */
  node: string;
  /** [x, y, z] extent in yd at scale 1. */
  size: readonly [number, number, number];
  boundsMin: readonly [number, number, number];
  boundsMax: readonly [number, number, number];
  pivot: string;
  /** Suggested uniform scale (RS-chunky: clutter 1.3x, Kenney nature about 3.5 to 4x). */
  scale: number;
  tris: number;
  tags: readonly string[];
  note?: string;
}

export interface VenomTextureAsset {
  id: string;
  kind: 'texture' | 'decal';
  pack: 'codex-generated';
  /** WebP paths relative to BASE_URL, keyed by pixel size. */
  files: { readonly 1024: string; readonly 512: string };
  /** Suggested world size (yd) of one repeat, or the decal's diameter. */
  tileYd: number;
  tags: readonly string[];
  note: string;
}

export type VenomAsset = VenomModelAsset | VenomTextureAsset;

export const VENOM_KITS = {
  kd: 'assets/venom/models/venom_kd_kit.glb',
  kh: 'assets/venom/models/venom_kh_kit.glb',
  kn: 'assets/venom/models/venom_kn_kit.glb',
} as const;

export const VENOM_MODELS: readonly VenomModelAsset[] = [
  {
    id: 'kd_stairs',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_stairs',
    size: [5, 5.1, 4],
    boundsMin: [-2.5, 0, 0],
    boundsMax: [2.5, 5.1, 4],
    pivot: 'base-offset',
    scale: 1,
    tris: 358,
    tags: ['stairs', 'decor', 'ledge', 'altar', 'dais'],
    note: 'Fixed 4 yd run x 5.1 yd rise; decorative only (ledge/altar), not for corridor flights.',
  },
  {
    id: 'kd_stairs_wide',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_stairs_wide',
    size: [7, 5.1, 4],
    boundsMin: [-3.5, 0, 0],
    boundsMax: [3.5, 5.1, 4],
    pivot: 'base-offset',
    scale: 1,
    tris: 358,
    tags: ['stairs', 'decor', 'dais', 'arenaSummit']
  },
  {
    id: 'kd_wall_arched',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_wall_arched',
    size: [4, 4, 1],
    boundsMin: [-2, 0, -0.5],
    boundsMax: [2, 4, 0.5],
    pivot: 'base-centre',
    scale: 1,
    tris: 724,
    tags: ['wall', 'archway', 'doorFrame']
  },
  {
    id: 'kd_wall_broken',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_wall_broken',
    size: [4, 4, 1],
    boundsMin: [-2, 0, -0.5],
    boundsMax: [2, 4, 0.5],
    pivot: 'base-centre',
    scale: 1,
    tris: 784,
    tags: ['wall', 'ruined', 'entrance']
  },
  {
    id: 'kd_wall_cracked',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_wall_cracked',
    size: [4, 4, 1.26],
    boundsMin: [-2, 0, -0.63],
    boundsMax: [2, 4, 0.63],
    pivot: 'base-centre',
    scale: 1,
    tris: 2010,
    tags: ['wall', 'ruined', 'overgrown', 'entrance']
  },
  {
    id: 'kd_wall_gated',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_wall_gated',
    size: [4, 4, 1],
    boundsMin: [-2, 0, -0.5],
    boundsMax: [2, 4, 0.5],
    pivot: 'base-centre',
    scale: 1,
    tris: 449,
    tags: ['wall', 'cage', 'pens', 'door']
  },
  {
    id: 'kd_wall_archedwindow_gated',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_wall_archedwindow_gated',
    size: [4, 4, 1],
    boundsMin: [-2, 0, -0.5],
    boundsMax: [2, 4, 0.5],
    pivot: 'base-centre',
    scale: 1,
    tris: 732,
    tags: ['wall', 'cage', 'pens']
  },
  {
    id: 'kd_wall_half',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_wall_half',
    size: [2, 4, 1],
    boundsMin: [0, 0, -0.5],
    boundsMax: [2, 4, 0.5],
    pivot: 'base-offset',
    scale: 1,
    tris: 272,
    tags: ['wall', 'endcap'],
    note: 'Pivot at one end: spans x 0..2.',
  },
  {
    id: 'kd_wall_pillar',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_wall_pillar',
    size: [4, 4, 1.5],
    boundsMin: [-2, 0, -0.75],
    boundsMax: [2, 4, 0.75],
    pivot: 'base-centre',
    scale: 1,
    tris: 540,
    tags: ['wall', 'pillar']
  },
  {
    id: 'kd_barrier',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_barrier',
    size: [4, 1.1, 0.5],
    boundsMin: [-2, 0, -0.25],
    boundsMax: [2, 1.1, 0.25],
    pivot: 'base-centre',
    scale: 1,
    tris: 90,
    tags: ['cage', 'pens', 'fence', 'arenaPit']
  },
  {
    id: 'kd_barrier_column',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_barrier_column',
    size: [4, 1.4, 0.7],
    boundsMin: [-2, 0, -0.35],
    boundsMax: [2, 1.4, 0.35],
    pivot: 'base-centre',
    scale: 1,
    tris: 110,
    tags: ['cage', 'pens', 'fence']
  },
  {
    id: 'kd_barrier_half',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_barrier_half',
    size: [2, 1.1, 0.5],
    boundsMin: [0, 0, -0.25],
    boundsMax: [2, 1.1, 0.25],
    pivot: 'base-offset',
    scale: 1,
    tris: 42,
    tags: ['cage', 'pens', 'fence'],
    note: 'Pivot at one end: spans x 0..2.',
  },
  {
    id: 'kd_pillar',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_pillar',
    size: [1.5, 4, 1.5],
    boundsMin: [-0.75, 0, -0.75],
    boundsMax: [0.75, 4, 0.75],
    pivot: 'base-centre',
    scale: 1,
    tris: 136,
    tags: ['pillar', 'archPost', 'door', 'entrance']
  },
  {
    id: 'kd_chest',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_chest',
    size: [1.7, 1.3, 1.45],
    boundsMin: [-0.85, 0, -0.7],
    boundsMax: [0.85, 1.3, 0.75],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 728,
    tags: ['treasure', 'clutter', 'hero']
  },
  {
    id: 'kd_chest_gold',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_chest_gold',
    size: [1.7, 1.3, 1.45],
    boundsMin: [-0.85, 0, -0.7],
    boundsMax: [0.85, 1.3, 0.75],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 1568,
    tags: ['treasure', 'gold', 'hero']
  },
  {
    id: 'kd_trunk_large_A',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_trunk_large_A',
    size: [1.5, 1, 1.3],
    boundsMin: [-0.75, 0, -0.65],
    boundsMax: [0.75, 1, 0.65],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 204,
    tags: ['treasure', 'barracks', 'clutter']
  },
  {
    id: 'kd_coin_stack_large',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_coin_stack_large',
    size: [1.44, 1.16, 1.66],
    boundsMin: [-0.72, 0, -0.82],
    boundsMax: [0.72, 1.16, 0.84],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 1776,
    tags: ['treasure', 'gold', 'hero']
  },
  {
    id: 'kd_coin_stack_medium',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_coin_stack_medium',
    size: [0.99, 0.64, 1.03],
    boundsMin: [-0.45, 0, -0.51],
    boundsMax: [0.55, 0.64, 0.53],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 968,
    tags: ['treasure', 'gold', 'clutter']
  },
  {
    id: 'kd_coin_stack_small',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_coin_stack_small',
    size: [0.96, 0.48, 0.9],
    boundsMin: [-0.45, 0, -0.46],
    boundsMax: [0.51, 0.48, 0.44],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 416,
    tags: ['treasure', 'gold', 'clutter']
  },
  {
    id: 'kd_candle_lit',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_candle_lit',
    size: [0.33, 1.05, 0.33],
    boundsMin: [-0.17, 0, -0.17],
    boundsMax: [0.17, 1.05, 0.16],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 120,
    tags: ['candle', 'light', 'ritual', 'clutter', 'offering']
  },
  {
    id: 'kd_candle_melted',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_candle_melted',
    size: [0.33, 0.7, 0.33],
    boundsMin: [-0.17, 0, -0.17],
    boundsMax: [0.17, 0.7, 0.16],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 66,
    tags: ['candle', 'ritual', 'clutter', 'offering']
  },
  {
    id: 'kd_candle_triple',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_candle_triple',
    size: [0.5, 0.87, 0.39],
    boundsMin: [-0.17, 0, -0.16],
    boundsMax: [0.33, 0.87, 0.23],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 198,
    tags: ['candle', 'light', 'ritual', 'clutter', 'offering']
  },
  {
    id: 'kd_shelf_small_candles',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_shelf_small_candles',
    size: [1, 0.99, 0.5],
    boundsMin: [-0.5, -0.3, 0],
    boundsMax: [0.5, 0.69, 0.5],
    pivot: 'mid-offset',
    scale: 1.3,
    tris: 231,
    tags: ['candle', 'wallDecor', 'ritual', 'treasure'],
    note: 'Wall-mounted: pivot on the wall face.',
  },
  {
    id: 'kd_bottle_A_green',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_bottle_A_green',
    size: [0.37, 0.89, 0.37],
    boundsMin: [-0.18, 0, -0.18],
    boundsMax: [0.18, 0.89, 0.18],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 144,
    tags: ['pools', 'bottle', 'clutter']
  },
  {
    id: 'kd_bottle_B_green',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_bottle_B_green',
    size: [0.55, 0.89, 0.55],
    boundsMin: [-0.28, 0, -0.28],
    boundsMax: [0.28, 0.89, 0.28],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 144,
    tags: ['pools', 'bottle', 'clutter']
  },
  {
    id: 'kd_bottle_A_labeled_green',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_bottle_A_labeled_green',
    size: [0.37, 0.89, 0.37],
    boundsMin: [-0.18, 0, -0.18],
    boundsMax: [0.18, 0.89, 0.18],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 144,
    tags: ['pools', 'bottle', 'clutter']
  },
  {
    id: 'kd_barrel_large_decorated',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_barrel_large_decorated',
    size: [1.94, 2.56, 1.86],
    boundsMin: [-0.92, 0, -0.92],
    boundsMax: [1.02, 2.55, 0.94],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 1047,
    tags: ['pools', 'vat', 'clutter']
  },
  {
    id: 'kd_barrel_small',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_barrel_small',
    size: [1, 1.02, 1],
    boundsMin: [-0.5, 0, -0.5],
    boundsMax: [0.5, 1.02, 0.5],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 207,
    tags: ['pens', 'hatchery', 'bucket', 'clutter']
  },
  {
    id: 'kd_barrel_small_stack',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_barrel_small_stack',
    size: [1.85, 1.77, 1],
    boundsMin: [-0.93, 0, -0.5],
    boundsMax: [0.93, 1.77, 0.5],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 1149,
    tags: ['barracks', 'clutter']
  },
  {
    id: 'kd_keg',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_keg',
    size: [1.8, 2.05, 2],
    boundsMin: [-0.9, 0, -0.98],
    boundsMax: [0.9, 2.05, 1.02],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 807,
    tags: ['pools', 'vat', 'clutter']
  },
  {
    id: 'kd_box_small_decorated',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_box_small_decorated',
    size: [1.49, 1.48, 1.49],
    boundsMin: [-0.61, 0, -0.63],
    boundsMax: [0.88, 1.48, 0.85],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 928,
    tags: ['treasure', 'clutter']
  },
  {
    id: 'kd_sword_shield',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_sword_shield',
    size: [2.23, 1.67, 0.34],
    boundsMin: [-1.12, -0.81, -0.09],
    boundsMax: [1.12, 0.85, 0.25],
    pivot: 'mid-centre',
    scale: 1.3,
    tris: 542,
    tags: ['barracks', 'wallDecor', 'weaponRack', 'hero'],
    note: 'Wall-mounted: pivot at mount point.',
  },
  {
    id: 'kd_sword_shield_broken',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_sword_shield_broken',
    size: [1.43, 1.96, 0.34],
    boundsMin: [-0.76, -0.79, -0.09],
    boundsMax: [0.67, 1.17, 0.25],
    pivot: 'mid-centre',
    scale: 1.3,
    tris: 396,
    tags: ['barracks', 'wallDecor', 'arenaPit'],
    note: 'Wall-mounted: pivot at mount point.',
  },
  {
    id: 'kd_table_long_broken',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_table_long_broken',
    size: [2.41, 1.27, 4.4],
    boundsMin: [-1.39, 0, -2.18],
    boundsMax: [1.02, 1.27, 2.23],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 550,
    tags: ['pens', 'troughs', 'hatchery', 'clutter']
  },
  {
    id: 'kd_table_long_tablecloth_decorated_A',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_table_long_tablecloth_decorated_A',
    size: [2.06, 1.89, 4],
    boundsMin: [-1.03, 0, -2],
    boundsMax: [1.03, 1.89, 2],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 2035,
    tags: ['barracks', 'warTable', 'hero']
  },
  {
    id: 'kd_table_medium_decorated_A',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_table_medium_decorated_A',
    size: [2, 1.89, 2],
    boundsMin: [-1, 0, -1],
    boundsMax: [1, 1.89, 1],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 1186,
    tags: ['pools', 'alchemy', 'clutter']
  },
  {
    id: 'kd_shelves',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_shelves',
    size: [2, 1.95, 0.5],
    boundsMin: [-1, 0.75, 0.25],
    boundsMax: [1, 2.7, 0.75],
    pivot: 'mid-offset',
    scale: 1.3,
    tris: 152,
    tags: ['barracks', 'wallDecor', 'weaponRack'],
    note: 'Wall-mounted: pivot at the wall face, hangs 0.75 to 2.7 yd up.',
  },
  {
    id: 'kd_bed_floor',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_bed_floor',
    size: [1.5, 0.56, 3],
    boundsMin: [-0.75, 0, -1.5],
    boundsMax: [0.75, 0.56, 1.5],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 232,
    tags: ['barracks', 'bedroll', 'clutter']
  },
  {
    id: 'kd_stool',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_stool',
    size: [0.75, 0.5, 0.75],
    boundsMin: [-0.37, 0, -0.38],
    boundsMax: [0.38, 0.5, 0.38],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 172,
    tags: ['barracks', 'clutter']
  },
  {
    id: 'kd_plate',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_plate',
    size: [0.97, 0.13, 0.97],
    boundsMin: [-0.48, 0, -0.49],
    boundsMax: [0.48, 0.13, 0.48],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 120,
    tags: ['pens', 'trough', 'clutter']
  },
  {
    id: 'kd_plate_food_A',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_plate_food_A',
    size: [1.02, 0.79, 0.97],
    boundsMin: [-0.54, 0, -0.49],
    boundsMax: [0.48, 0.79, 0.48],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 547,
    tags: ['barracks', 'clutter']
  },
  {
    id: 'kd_keyring_hanging',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_keyring_hanging',
    size: [0.68, 1.22, 0.38],
    boundsMin: [-0.34, -1.2, -0.16],
    boundsMax: [0.34, 0.02, 0.23],
    pivot: 'top-centre',
    scale: 1.3,
    tris: 728,
    tags: ['pens', 'wallDecor'],
    note: 'Hangs down from the pivot (hook point).',
  },
  {
    id: 'kd_key',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_key',
    size: [0.9, 0.4, 0.14],
    boundsMin: [-0.2, -0.2, -0.07],
    boundsMax: [0.7, 0.2, 0.07],
    pivot: 'mid-offset',
    scale: 1.3,
    tris: 160,
    tags: ['treasure', 'clutter']
  },
  {
    id: 'kd_torch_mounted',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_torch_mounted',
    size: [0.55, 1.06, 0.62],
    boundsMin: [-0.28, -0.38, 0],
    boundsMax: [0.28, 0.68, 0.62],
    pivot: 'mid-offset',
    scale: 1.3,
    tris: 278,
    tags: ['wallDecor', 'light', 'torch'],
    note: 'Wall-mounted: pivot at the wall face, extends +z.',
  },
  {
    id: 'kd_banner_thin_green',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_banner_thin_green',
    size: [1.1, 3.17, 0.36],
    boundsMin: [-0.55, 0.56, 0.35],
    boundsMax: [0.55, 3.73, 0.72],
    pivot: 'mid-offset',
    scale: 1,
    tris: 83,
    tags: ['banner', 'jade', 'wallDecor', 'entrance', 'ritual']
  },
  {
    id: 'kd_banner_patternA_green',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_banner_patternA_green',
    size: [1.5, 3.2, 0.31],
    boundsMin: [-0.75, 0.53, 0.38],
    boundsMax: [0.75, 3.73, 0.69],
    pivot: 'mid-offset',
    scale: 1,
    tris: 110,
    tags: ['banner', 'jade', 'wallDecor', 'barracks']
  },
  {
    id: 'kd_banner_triple_green',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_banner_triple_green',
    size: [3.7, 3.2, 0.36],
    boundsMin: [-1.85, 0.53, 0.35],
    boundsMax: [1.85, 3.73, 0.72],
    pivot: 'mid-offset',
    scale: 1,
    tris: 263,
    tags: ['banner', 'jade', 'wallDecor', 'barracks', 'hero']
  },
  {
    id: 'kd_banner_shield_green',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_banner_shield_green',
    size: [2.23, 3.2, 0.37],
    boundsMin: [-1.12, 0.53, 0.32],
    boundsMax: [1.12, 3.73, 0.69],
    pivot: 'mid-offset',
    scale: 1,
    tris: 639,
    tags: ['banner', 'jade', 'wallDecor', 'barracks']
  },
  {
    id: 'kd_banner_triple_red',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_banner_triple_red',
    size: [3.7, 3.2, 0.36],
    boundsMin: [-1.85, 0.53, 0.35],
    boundsMax: [1.85, 3.73, 0.72],
    pivot: 'mid-offset',
    scale: 1,
    tris: 263,
    tags: ['banner', 'blood', 'wallDecor', 'arenaSummit', 'ritual']
  },
  {
    id: 'kd_banner_patternC_red',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_banner_patternC_red',
    size: [1.5, 3.2, 0.31],
    boundsMin: [-0.75, 0.53, 0.38],
    boundsMax: [0.75, 3.73, 0.69],
    pivot: 'mid-offset',
    scale: 1,
    tris: 97,
    tags: ['banner', 'blood', 'wallDecor', 'arenaSummit']
  },
  {
    id: 'kd_banner_patternB_yellow',
    kind: 'model',
    pack: 'kaykit-dungeon-remastered',
    file: 'assets/venom/models/venom_kd_kit.glb',
    node: 'kd_banner_patternB_yellow',
    size: [1.5, 3.2, 0.31],
    boundsMin: [-0.75, 0.53, 0.38],
    boundsMax: [0.75, 3.73, 0.69],
    pivot: 'mid-offset',
    scale: 1,
    tris: 97,
    tags: ['banner', 'gold', 'wallDecor', 'treasure']
  },
  {
    id: 'kh_bone_A',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_bone_A',
    size: [0.71, 0.28, 0.2],
    boundsMin: [-0.35, -0.14, -0.1],
    boundsMax: [0.35, 0.14, 0.1],
    pivot: 'mid-centre',
    scale: 1.3,
    tris: 204,
    tags: ['bone', 'clutter', 'arenaPit', 'hatchery']
  },
  {
    id: 'kh_bone_B',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_bone_B',
    size: [0.61, 0.17, 0.29],
    boundsMin: [-0.3, -0.09, -0.14],
    boundsMax: [0.31, 0.09, 0.14],
    pivot: 'top-centre',
    scale: 1.3,
    tris: 220,
    tags: ['bone', 'clutter', 'arenaPit', 'hatchery']
  },
  {
    id: 'kh_bone_C',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_bone_C',
    size: [1.11, 0.28, 0.2],
    boundsMin: [-0.55, -0.14, -0.1],
    boundsMax: [0.55, 0.14, 0.1],
    pivot: 'mid-centre',
    scale: 1.3,
    tris: 204,
    tags: ['bone', 'clutter', 'arenaPit', 'hatchery']
  },
  {
    id: 'kh_skull',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_skull',
    size: [0.92, 0.9, 0.91],
    boundsMin: [-0.46, 0.02, -0.46],
    boundsMax: [0.46, 0.92, 0.45],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 344,
    tags: ['bone', 'skull', 'clutter', 'arenaPit', 'skullMound']
  },
  {
    id: 'kh_skull_candle',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_skull_candle',
    size: [0.92, 1.19, 0.94],
    boundsMin: [-0.46, 0.06, -0.52],
    boundsMax: [0.46, 1.25, 0.42],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 542,
    tags: ['bone', 'skull', 'candle', 'light', 'ritual', 'offering']
  },
  {
    id: 'kh_ribcage',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_ribcage',
    size: [0.85, 0.86, 0.75],
    boundsMin: [-0.42, -0.39, -0.42],
    boundsMax: [0.42, 0.47, 0.33],
    pivot: 'mid-centre',
    scale: 1.3,
    tris: 1240,
    tags: ['bone', 'hero', 'arenaPit', 'beastRibcage'],
    note: 'Pivot at centre; scale x3 for the Ravager hero ribcage (bible 5.G). Lift by 0.39*scale to sit on the ground.',
  },
  {
    id: 'kh_post_skull',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_post_skull',
    size: [0.81, 3.3, 1.57],
    boundsMin: [-0.41, 0, -0.22],
    boundsMax: [0.4, 3.3, 1.35],
    pivot: 'base-offset',
    scale: 1.3,
    tris: 776,
    tags: ['totem', 'skull', 'arenaSummit', 'ritual', 'hero'],
    note: 'Skull hangs toward +z.',
  },
  {
    id: 'kh_shrine',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_shrine',
    size: [1.1, 2.4, 1.1],
    boundsMin: [-0.55, 0, -0.55],
    boundsMax: [0.55, 2.4, 0.55],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 94,
    tags: ['shrine', 'ritual', 'offering', 'hero']
  },
  {
    id: 'kh_shrine_candles',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_shrine_candles',
    size: [1.1, 1.79, 1.1],
    boundsMin: [-0.55, 0, -0.55],
    boundsMax: [0.55, 1.79, 0.55],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 360,
    tags: ['shrine', 'candle', 'ritual', 'offering', 'arenaSummit']
  },
  {
    id: 'kh_plaque_candles',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_plaque_candles',
    size: [2, 1.13, 2],
    boundsMin: [-1, 0, -1],
    boundsMax: [1, 1.13, 1],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 418,
    tags: ['candle', 'ritual', 'offering']
  },
  {
    id: 'kh_candle_triple',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_candle_triple',
    size: [0.45, 0.79, 0.35],
    boundsMin: [-0.15, 0, -0.14],
    boundsMax: [0.3, 0.79, 0.21],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 198,
    tags: ['candle', 'ritual', 'clutter']
  },
  {
    id: 'kh_arch',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_arch',
    size: [4.22, 4.41, 0.77],
    boundsMin: [-2.11, 0, -0.39],
    boundsMax: [2.11, 4.41, 0.39],
    pivot: 'base-centre',
    scale: 1,
    tris: 436,
    tags: ['archway', 'door']
  },
  {
    id: 'kh_arch_gate',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_arch_gate',
    size: [4.22, 4.41, 0.77],
    boundsMin: [-2.11, 0, -0.39],
    boundsMax: [2.11, 4.41, 0.39],
    pivot: 'base-centre',
    scale: 1,
    tris: 868,
    tags: ['archway', 'door', 'gate']
  },
  {
    id: 'kh_lantern_standing',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_lantern_standing',
    size: [0.64, 0.93, 0.64],
    boundsMin: [-0.32, 0, -0.32],
    boundsMax: [0.32, 0.93, 0.32],
    pivot: 'base-centre',
    scale: 1.3,
    tris: 264,
    tags: ['light', 'lantern', 'clutter']
  },
  {
    id: 'kh_tree_dead_small',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_tree_dead_small',
    size: [1.41, 2.99, 1.02],
    boundsMin: [-1.2, -0.3, -0.81],
    boundsMax: [0.21, 2.69, 0.21],
    pivot: 'mid-offset',
    scale: 1.3,
    tris: 184,
    tags: ['overgrowth', 'roots', 'entrance'],
    note: 'Trunk base at origin, leans -x.',
  },
  {
    id: 'kh_tree_dead_medium',
    kind: 'model',
    pack: 'kaykit-halloween-bits',
    file: 'assets/venom/models/venom_kh_kit.glb',
    node: 'kh_tree_dead_medium',
    size: [1.81, 4.19, 0.47],
    boundsMin: [-0.48, -0.3, -0.23],
    boundsMax: [1.32, 3.89, 0.23],
    pivot: 'mid-offset',
    scale: 1.3,
    tris: 216,
    tags: ['overgrowth', 'roots', 'entrance', 'hero'],
    note: 'Trunk base at origin, leans +x.',
  },
  {
    id: 'kn_plant_bush',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_plant_bush',
    size: [0.4, 0.24, 0.4],
    boundsMin: [-0.2, -0.05, -0.2],
    boundsMax: [0.2, 0.19, 0.2],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 32,
    tags: ['overgrowth', 'bush']
  },
  {
    id: 'kn_plant_bushDetailed',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_plant_bushDetailed',
    size: [0.6, 0.36, 0.6],
    boundsMin: [-0.3, -0.05, -0.3],
    boundsMax: [0.3, 0.31, 0.3],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 104,
    tags: ['overgrowth', 'fern']
  },
  {
    id: 'kn_plant_bushLarge',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_plant_bushLarge',
    size: [0.37, 0.24, 0.34],
    boundsMin: [-0.19, -0.05, -0.17],
    boundsMax: [0.19, 0.19, 0.17],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 60,
    tags: ['overgrowth', 'bush']
  },
  {
    id: 'kn_plant_bushSmall',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_plant_bushSmall',
    size: [0.38, 0.21, 0.34],
    boundsMin: [-0.19, -0.05, -0.17],
    boundsMax: [0.19, 0.16, 0.17],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 16,
    tags: ['overgrowth', 'bush']
  },
  {
    id: 'kn_plant_flatShort',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_plant_flatShort',
    size: [0.28, 0.23, 0.28],
    boundsMin: [-0.14, -0.05, -0.14],
    boundsMax: [0.14, 0.18, 0.14],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 44,
    tags: ['overgrowth', 'fern']
  },
  {
    id: 'kn_plant_flatTall',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_plant_flatTall',
    size: [0.27, 0.28, 0.27],
    boundsMin: [-0.14, -0.05, -0.14],
    boundsMax: [0.14, 0.23, 0.14],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 32,
    tags: ['overgrowth', 'fern']
  },
  {
    id: 'kn_grass_large',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_grass_large',
    size: [0.41, 0.25, 0.41],
    boundsMin: [-0.2, -0.05, -0.21],
    boundsMax: [0.21, 0.2, 0.2],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 224,
    tags: ['overgrowth', 'grass']
  },
  {
    id: 'kn_grass_leafs',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_grass_leafs',
    size: [0.23, 0.14, 0.26],
    boundsMin: [-0.12, -0.05, -0.13],
    boundsMax: [0.11, 0.09, 0.12],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 36,
    tags: ['overgrowth', 'grass']
  },
  {
    id: 'kn_grass_leafsLarge',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_grass_leafsLarge',
    size: [0.48, 0.14, 0.49],
    boundsMin: [-0.24, -0.05, -0.24],
    boundsMax: [0.23, 0.09, 0.24],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 144,
    tags: ['overgrowth', 'grass']
  },
  {
    id: 'kn_hanging_moss',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_hanging_moss',
    size: [0.16, 0.48, 0.03],
    boundsMin: [-0.08, -0.05, 0.47],
    boundsMax: [0.08, 0.43, 0.5],
    pivot: 'base-offset',
    scale: 3.5,
    tris: 52,
    tags: ['overgrowth', 'vines', 'wallDecor'],
    note: 'Hangs off an edge at +z 0.5; place at a wall top/ledge lip.',
  },
  {
    id: 'kn_mushroom_tanGroup',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_mushroom_tanGroup',
    size: [0.27, 0.25, 0.25],
    boundsMin: [-0.14, -0.05, -0.11],
    boundsMax: [0.13, 0.2, 0.14],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 144,
    tags: ['overgrowth', 'mushroom', 'hatchery']
  },
  {
    id: 'kn_log',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_log',
    size: [0.23, 0.17, 0.71],
    boundsMin: [-0.11, -0.05, -0.35],
    boundsMax: [0.13, 0.12, 0.35],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 200,
    tags: ['overgrowth', 'clutter']
  },
  {
    id: 'kn_stump_old',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_stump_old',
    size: [0.36, 0.27, 0.37],
    boundsMin: [-0.16, -0.05, -0.18],
    boundsMax: [0.2, 0.22, 0.18],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 120,
    tags: ['overgrowth', 'clutter']
  },
  {
    id: 'kn_stump_oldTall',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_stump_oldTall',
    size: [0.36, 0.67, 0.37],
    boundsMin: [-0.16, -0.05, -0.18],
    boundsMax: [0.2, 0.62, 0.18],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 120,
    tags: ['overgrowth', 'clutter']
  },
  {
    id: 'kn_rock_largeA',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_rock_largeA',
    size: [0.78, 0.26, 1.02],
    boundsMin: [-0.39, -0.05, -0.51],
    boundsMax: [0.39, 0.21, 0.51],
    pivot: 'base-centre',
    scale: 4,
    tris: 80,
    tags: ['rock', 'rubble', 'entrance']
  },
  {
    id: 'kn_rock_largeC',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_rock_largeC',
    size: [1.06, 0.32, 1.02],
    boundsMin: [-0.53, -0.05, -0.51],
    boundsMax: [0.53, 0.27, 0.51],
    pivot: 'base-centre',
    scale: 4,
    tris: 72,
    tags: ['rock', 'rubble', 'entrance']
  },
  {
    id: 'kn_rock_smallA',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_rock_smallA',
    size: [0.36, 0.19, 0.36],
    boundsMin: [-0.18, -0.05, -0.18],
    boundsMax: [0.18, 0.14, 0.18],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 16,
    tags: ['rock', 'rubble', 'clutter']
  },
  {
    id: 'kn_rock_smallFlatA',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_rock_smallFlatA',
    size: [0.5, 0.07, 0.43],
    boundsMin: [-0.25, -0.05, -0.21],
    boundsMax: [0.25, 0.01, 0.21],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 20,
    tags: ['rock', 'rubble', 'clutter']
  },
  {
    id: 'kn_rock_tallA',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_rock_tallA',
    size: [0.98, 1, 0.68],
    boundsMin: [-0.49, -0.05, -0.34],
    boundsMax: [0.49, 0.95, 0.34],
    pivot: 'base-centre',
    scale: 4,
    tris: 136,
    tags: ['rock', 'rubble', 'hero', 'entrance']
  },
  {
    id: 'kn_stone_largeA',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_stone_largeA',
    size: [0.78, 0.26, 1.02],
    boundsMin: [-0.39, -0.05, -0.51],
    boundsMax: [0.39, 0.21, 0.51],
    pivot: 'base-centre',
    scale: 4,
    tris: 80,
    tags: ['rock', 'rubble']
  },
  {
    id: 'kn_stone_tallA',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_stone_tallA',
    size: [0.98, 1, 0.68],
    boundsMin: [-0.49, -0.05, -0.34],
    boundsMax: [0.49, 0.95, 0.34],
    pivot: 'base-centre',
    scale: 4,
    tris: 136,
    tags: ['rock', 'rubble']
  },
  {
    id: 'kn_statue_head',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_statue_head',
    size: [0.73, 1, 0.55],
    boundsMin: [-0.36, -0.05, -0.31],
    boundsMax: [0.36, 0.95, 0.24],
    pivot: 'base-centre',
    scale: 4,
    tris: 196,
    tags: ['statue', 'idol', 'entrance', 'hero']
  },
  {
    id: 'kn_statue_obelisk',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_statue_obelisk',
    size: [0.31, 0.88, 0.31],
    boundsMin: [-0.15, -0.05, -0.15],
    boundsMax: [0.15, 0.82, 0.15],
    pivot: 'base-centre',
    scale: 4,
    tris: 38,
    tags: ['statue', 'ritual']
  },
  {
    id: 'kn_statue_columnDamaged',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_statue_columnDamaged',
    size: [0.3, 0.7, 0.3],
    boundsMin: [-0.15, -0.05, -0.15],
    boundsMax: [0.15, 0.65, 0.15],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 108,
    tags: ['ruined', 'column', 'entrance']
  },
  {
    id: 'kn_statue_block',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_statue_block',
    size: [0.4, 0.4, 0.4],
    boundsMin: [-0.2, -0.05, -0.2],
    boundsMax: [0.2, 0.35, 0.2],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 140,
    tags: ['ruined', 'rubble']
  },
  {
    id: 'kn_pot_large',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_pot_large',
    size: [0.56, 0.2, 0.49],
    boundsMin: [-0.28, -0.05, -0.24],
    boundsMax: [0.28, 0.15, 0.24],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 68,
    tags: ['urn', 'offering', 'clutter']
  },
  {
    id: 'kn_pot_small',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_pot_small',
    size: [0.32, 0.27, 0.28],
    boundsMin: [-0.16, -0.05, -0.14],
    boundsMax: [0.16, 0.22, 0.14],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 72,
    tags: ['urn', 'offering', 'clutter']
  },
  {
    id: 'kn_lily_large',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_lily_large',
    size: [0.27, 0.1, 0.31],
    boundsMin: [-0.14, -0.05, -0.16],
    boundsMax: [0.14, 0.05, 0.16],
    pivot: 'base-centre',
    scale: 3.5,
    tris: 86,
    tags: ['pools', 'lilypad']
  },
  {
    id: 'kn_tree_palmDetailedShort',
    kind: 'model',
    pack: 'kenney-nature-kit',
    file: 'assets/venom/models/venom_kn_kit.glb',
    node: 'kn_tree_palmDetailedShort',
    size: [1.03, 1.12, 1.04],
    boundsMin: [-0.52, -0.05, -0.52],
    boundsMax: [0.52, 1.07, 0.52],
    pivot: 'base-centre',
    scale: 4,
    tris: 336,
    tags: ['overgrowth', 'tree', 'entrance', 'hero']
  }
] as const;

export const VENOM_TEXTURES: readonly VenomTextureAsset[] = [
  {
    id: 'venom_floor_flagstone',
    kind: 'texture',
    pack: 'codex-generated',
    files: {
      512: 'assets/venom/textures/venom_floor_flagstone_512.webp',
      1024: 'assets/venom/textures/venom_floor_flagstone_1024.webp',
    },
    tileYd: 6,
    tags: ['floor', 'flagstone', 'atlasCell0', 'atlasCell3', 'entrance', 'ritual', 'corridor', 'pools', 'arenaSanctum'],
    note: 'Seamless both axes. Graded to mean #545449, 32.7% luminance, 14% saturation. For atlas cell 3 (wet), multiply by 0.85.',
  },
  {
    id: 'venom_floor_dirt_roots',
    kind: 'texture',
    pack: 'codex-generated',
    files: {
      512: 'assets/venom/textures/venom_floor_dirt_roots_512.webp',
      1024: 'assets/venom/textures/venom_floor_dirt_roots_1024.webp',
    },
    tileYd: 6,
    tags: ['floor', 'dirt', 'atlasCell1', 'pens', 'hatchery', 'arenaPit'],
    note: 'Seamless both axes. Mean #584c42, 30.5% luminance.',
  },
  {
    id: 'venom_floor_temple_tiles',
    kind: 'texture',
    pack: 'codex-generated',
    files: {
      512: 'assets/venom/textures/venom_floor_temple_tiles_512.webp',
      1024: 'assets/venom/textures/venom_floor_temple_tiles_1024.webp',
    },
    tileYd: 8,
    tags: ['floor', 'tiles', 'atlasCell2', 'barracks', 'treasure', 'ritual', 'arenaSummit'],
    note: 'Exact 4x4 tile grid (2 yd tiles at 8 yd per repeat). Seamless. Mean #4f4f45, 30.7% luminance, 14% saturation.',
  },
  {
    id: 'venom_wall_sandstone',
    kind: 'texture',
    pack: 'codex-generated',
    files: {
      512: 'assets/venom/textures/venom_wall_sandstone_512.webp',
      1024: 'assets/venom/textures/venom_wall_sandstone_1024.webp',
    },
    tileYd: 4,
    tags: ['wall', 'sandstone', 'skirt', 'cliff'],
    note: '4 block courses per repeat (1 yd courses at 4 yd). Seamless. Mean #866f4a. Tint toward stone.cliff #4a4236 for skirts.',
  },
  {
    id: 'venom_trim_serpent',
    kind: 'texture',
    pack: 'codex-generated',
    files: {
      512: 'assets/venom/textures/venom_trim_serpent_512.webp',
      1024: 'assets/venom/textures/venom_trim_serpent_1024.webp',
    },
    tileYd: 2.4,
    tags: ['trim', 'frieze', 'jade', 'lip', 'lintel', 'stairCheek'],
    note: '4 identical frieze bands stacked; one band = V 0..0.25. Seamless both axes (U repeats horizontally).',
  },
  {
    id: 'venom_water_poison',
    kind: 'texture',
    pack: 'codex-generated',
    files: {
      512: 'assets/venom/textures/venom_water_poison_512.webp',
      1024: 'assets/venom/textures/venom_water_poison_1024.webp',
    },
    tileYd: 6,
    tags: ['water', 'poison', 'pools', 'nonWalkable'],
    note: 'Seamless (offset-blended in X). Mean #20382e, 19.6% luminance; scum is olive, not venom lime. Pan two layers for motion.',
  },
  {
    id: 'venom_wood_planks',
    kind: 'texture',
    pack: 'codex-generated',
    files: {
      512: 'assets/venom/textures/venom_wood_planks_512.webp',
      1024: 'assets/venom/textures/venom_wood_planks_1024.webp',
    },
    tileYd: 3,
    tags: ['wood', 'gate', 'cage', 'portcullis', 'door'],
    note: 'Vertical planks with iron straps. Seamless in X; straps are unevenly spaced in Y but tile without a seam.',
  },
  {
    id: 'venom_decal_mosaic',
    kind: 'decal',
    pack: 'codex-generated',
    files: {
      512: 'assets/venom/textures/venom_decal_mosaic_512.webp',
      1024: 'assets/venom/textures/venom_decal_mosaic_1024.webp',
    },
    tileYd: 28,
    tags: ['decal', 'floor', 'mosaic', 'medallion', 'arenaSanctum', 'arenaSummit', 'hero'],
    note: 'RGBA, not tiled. Low-contrast (value range compressed to 50%, mean 31% luminance) so it never reads as a telegraph ring. Feathered circular alpha. Sized for a radius-14 yd disc.',
  },
  {
    id: 'venom_decal_serpent_face',
    kind: 'decal',
    pack: 'codex-generated',
    files: {
      512: 'assets/venom/textures/venom_decal_serpent_face_512.webp',
      1024: 'assets/venom/textures/venom_decal_serpent_face_1024.webp',
    },
    tileYd: 6,
    tags: ['decal', 'relief', 'serpentFace', 'slabDoor', 'seal', 'arenaSummit', 'door', 'hero'],
    note: 'RGBA cobra-hood relief with gold trim and jade eyes for the Chieftain stone slab doors / fang-jaw facade. Background alpha 0; interior holes kept opaque.',
  }
] as const;

export const VENOM_ASSETS: readonly VenomAsset[] = [...VENOM_MODELS, ...VENOM_TEXTURES];

const byId = new Map<string, VenomAsset>(VENOM_ASSETS.map((a) => [a.id, a]));
export const venomAsset = (id: string): VenomAsset | undefined => byId.get(id);
export const venomAssetsTagged = (...tags: string[]): VenomAsset[] => VENOM_ASSETS.filter((a) => tags.every((t) => a.tags.includes(t)));
